import { useState } from 'react';

import { PillButtons, Button } from '../components/input';
import { Code, CodeBlock } from '../components/code';
import { LeftNav } from '../components/nav';
import { useGlobal } from '../state';

const codeStyle = { maxWidth: 640 };

const Step = ({ number, children, text }) => (
  <div>
    <h2>
      Step {number}
      {text && `: ${text}`}
    </h2>
    {children}
  </div>
);

const Python = ({ apiKey }) => {
  const crawlCode = `from fetchfox_sdk import FetchFox

fox = FetchFox(api_key="${apiKey}")

crawl_data = fox.crawl({
    "pattern": "https://pokemondb.net/pokedex/char*",
    "max_visits": 20,
})

print("Found URLs:", crawl_data["results"]["hits"])`;

  const extractCode = `${crawlCode}

# Use the URLs from crawl for extract step
extract_data = fox.extract({
    "urls": crawl_data["results"]["hits"],
    "template": {
        "name": "Pokemon name",
        "number": "Pokemon number",
        "stats": "Pokemon basic stats as a dictionary",
    }
})

print("Extracted items:", crawl_data["results"]["items"])`;

  const scrapeCode = `from fetchfox_sdk import FetchFox

fox = FetchFox(api_key="${apiKey}")

data = fox.scrape({
    "pattern": "https://pokemondb.net/pokedex/char*",
    "max_visits": 20,
    "template": {
        "name": "Pokemon name",
        "number": "Pokemon number",
        "stats": "Pokemon basic stats as a dictionary",
    }
})

print("Found URLs:", crawl_data["results"]["hits"])
print("Extracted items:", crawl_data["results"]["items"])`;

  return (
    <div>
      <Step number="1">
        Install the pip package.
        <Code language="shell" code="pip install fetchfox-sdk" copy style={codeStyle} />
      </Step>
      <Step number="2" text="Crawl">
        Run a crawl to find URLs matching a pattern.
        <Code language="python" code={crawlCode} copy style={codeStyle} />
      </Step>
      <Step number="3" text="Extract">
        Extract data from those URLs.
        <Code language="python" code={extractCode} copy style={codeStyle} />
      </Step>
      <Step number="4" text="Scrape = Crawl + Extract">
        Optional: Use scrape to crawl and extract in one step.
        <Code language="python" code={scrapeCode} copy style={codeStyle} />
      </Step>
    </div>
  );
};

const JavaScript = ({ apiKey }) => {
  const importCode = `import * as fox from 'fetchfox-sdk';

fox.configure({ apiKey: '${apiKey}' });`;

  const crawlCode = `${importCode}

const crawlData = await fox.crawl({
  pattern: 'https://pokemondb.net/pokedex/char*',
  maxVisits: 20,
});

console.log('Found urls:', crawlData.results.hits);`;

  const extractCode = `${crawlCode}

// Use the URLs from crawl for extract step
const extractData = await fox.extract({
    urls: crawlData.results.hits,
    template: {
        name: 'Pokemon name',
        number: 'Pokemon number',
        stats: 'Pokemon basic stats as a dictionary',
    }
});

console.log('Extracted items:', extractData.results.items);`;

  const scrapeCode = `${importCode}

const scrapeData = await fox.scrape({
    pattern: 'https://pokemondb.net/pokedex/char*',
    maxVisits: 5,
    template: {
        name: 'Pokemon name',
        number: 'Pokemon number',
        stats: 'Pokemon basic stats as a dictionary',
    }
});

console.log('Found URLs:', scrapeData.results.hits);
console.log('Extracted items:', scrapeData.results.items);`;

  return (
    <div>
      <Step number="1">
        Install the npm package.
        <Code language="shell" code="npm install fetchfox-sdk" copy style={codeStyle} />
      </Step>
      <Step number="2" text="Crawl">
        Run a crawl to find URLs matching a pattern.
        <Code language="javascript" code={crawlCode} copy style={codeStyle} />
      </Step>
      <Step number="3" text="Extract">
        Extract data from those URLs.
        <Code language="javascript" code={extractCode} copy style={codeStyle} />
      </Step>
      <Step number="4" text="Scrape = Crawl + Extract">
        Optional: Use scrape to crawl and extract in one step
        <Code language="javascript" code={scrapeCode} copy style={codeStyle} />
      </Step>
    </div>
  );
};

const Http = ({ apiKey }) => {
  const crawlCode = `curl -X POST https://api.fetchfox.ai/api/crawl \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '{
        "pattern":"https://pokemondb.net/pokedex/char*",
        "max_visits": 20
      }'

# Matching URLs are in .results.hits`;

  const extractCode = `curl -X POST https://api.fetchfox.ai/api/extract \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '{
        "urls":[
          "https://pokemondb.net/pokedex/charcadet",
          "https://pokemondb.net/pokedex/charizard",
          "https://pokemondb.net/pokedex/charjabug",
          "https://pokemondb.net/pokedex/charmander",
          "https://pokemondb.net/pokedex/charmeleon"
        ],
        "template": {
           "name": "Pokemon name",
           "number": "Pokemon number",
           "stats": "Pokemon basic stats as a dictionary"
        }
      }'

# Extracted data is in .results.items`;

  const scrapeCode = `curl -X POST https://api.fetchfox.ai/api/scrape \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '{
        "pattern":"https://pokemondb.net/pokedex/char*",
        "max_visits": 20,
        "template": {
           "name": "Pokemon name",
           "number": "Pokemon number",
           "stats": "Pokemon basic stats as a dictionary"
        }
      }'

# Matching URLs are in .results.hits
# and extracted data is in .results.items`;

  return (
    <div>
      <Step number="1" text="Crawl">
        Run a crawl to find URLs matching a pattern.
        <Code language="bash" code={crawlCode} copy style={codeStyle} />
      </Step>
      <Step number="2" text="Extract">
        Extract data from those URLs.
        <Code language="bash" code={extractCode} copy style={codeStyle} />
      </Step>
      <Step number="3" text="Scrape = Crawl + Extract">
        Optional: Use scrape to crawl and extract in one step
        <Code language="bash" code={scrapeCode} copy style={codeStyle} />
      </Step>
    </div>
  );
};

export default function QuickStart() {
  const { apiKey } = useGlobal();
  const [tab, setTab] = useState('http');

  const choices = [
    ['http', 'HTTP'],
    ['python', 'Python'],
    ['javascript', 'JavaScript'],
  ];

  const node =
    tab &&
    {
      http: <Http apiKey={apiKey} />,
      python: <Python apiKey={apiKey} />,
      javascript: <JavaScript apiKey={apiKey} />,
    }[tab];

  const doneNode = tab && (
    <div>
      <h2>All done!</h2>
      <p>That's all there is to it.</p>
      <p>
        From here, read our{' '}
        <a className="bold" href="https://docs.fetchfox.ai">
          documentation
        </a>{' '}
        for more information, or continue to your dashboard.
      </p>

      <div style={{ margin: '40px 0', display: 'flex', justifyContent: 'center', gap: 10 }}>
        <Button style={{ width: 220 }} href="https://docs.fetchfox.ai" outline large>
          Docs
        </Button>
        <Button style={{ width: 220 }} to="/dashboard" large>
          Dashboard
        </Button>
      </div>
    </div>
  );

  return (
    <LeftNav>
      <h2>Quick start</h2>
      <p>
        Your API key is <Code language="text" inline code={apiKey} copy />
      </p>

      <h2>Pick a language</h2>
      <p>How would you like to use FetchFox?</p>

      <PillButtons choices={choices} value={tab} onChange={setTab} />

      <br />

      {node}
      {doneNode}
    </LeftNav>
  );
}
